import { useGSAP } from '@gsap/react';
import { PrismicRichText } from '@prismicio/react';
import AniLink from 'gatsby-plugin-transition-link/AniLink';
import { gsap } from 'gsap';
import React from 'react';
import styled from 'styled-components';

import FacebookIcon from '../assets/svg/svg--facebook.inline.svg';
import LinkedinIcon from '../assets/svg/svg--linkedin.inline.svg';
import TwitterIcon from '../assets/svg/svg--twitter.inline.svg';
import { useShortcodes } from '../hooks/useShortcodes';
import { useTableOfContents } from '../hooks/useTableOfContents';
import { media } from '../styles/media';
import { getBlogDisplayDate } from '../utils/dates';
import { wpBlogSerializer } from '../utils/shortcodeSerializers';
import { Button } from './ui/Button';

const Banner = styled.div`
  .eyebrow p {
    display: inline-block;
  }
  .eyebrow.line::after {
    margin-top: 8px;
  }
`;

const BlogContent = styled.section`
  .gatsby-image-wrapper {
    overflow: visible;
    div:not(.gatsby-image-wrapper) {
      display: none !important;
    }
    img {
      position: static;
      top: auto;
      left: auto;
      height: auto;
      width: 100%;
      display: block;
    }
  }
  table {
    margin-bottom: 20px;
    width: 100%;
    td {
      border: 1px solid var(--grey-3);
      padding: 12px;
    }
  }
  blockquote {
    padding-left: 1em;
    border-left: 3px solid var(--grey-3);
  }
  h2,
  h3 {
    font-weight: bold;
    margin-bottom: 20px;
  }
  h2 {
    font-size: 24px;
    text-transform: capitalize;
  }
  h3 {
    font-size: 16px;
  }
  ul,
  ol {
    margin-bottom: 20px;
    padding-left: 1.2em;
  }
  ul li {
    list-style: disc;
  }
  ol li {
    list-style: decimal;
  }
  .wp-block-embed.is-type-video {
    margin-bottom: 20px;
    .wp-block-embed__wrapper {
      position: relative;
      &::before {
        content: '';
        display: block;
        padding-bottom: 56.5%;
        width: 100%;
      }
    }
    iframe {
      position: absolute;
      height: 100%;
      left: 0;
      top: 0;
      width: 100%;
    }
  }
`;

const BlockPost = (props) => {
  useGSAP(() => {
    const fadeUp = document.querySelectorAll('.fade-up');

    fadeUp.forEach(function (element) {
      gsap.from(element, {
        scrollTrigger: {
          trigger: element,
          start: 'top 90%',
        },
        duration: 0.5,
        opacity: '0',
        y: '20%',
      });
    });

    // sticky Table of Contents
    gsap.matchMedia().add(media.smUp, () => {
      gsap.to('.table-of-contents', {
        scrollTrigger: {
          trigger: '.table-of-contents',
          endTrigger: '.blog-grid',
          start: '-100px top', // 68px = Nav height
          end: 'bottom top',
          pin: '.table-of-contents',
          pinnedContainer: '.blog-grid',
        },
      });
    });
  });

  const post = props?.post;
  const banner = props?.banner?.primary;

  if (post === '' || post?.content === null) return null;
  const { content, headings2 } = useTableOfContents(post.content);
  const parsedContent = useShortcodes(content, wpBlogSerializer);

  return (
    <div className="[&_.blog_a]:underline">
      <div className="container pt-16 md:pt-20 lg:pt-32">
        <div className="blog-grid grid grid-cols-4 md:grid-cols-12">
          <div className="col-span-4 md:col-span-5">
            <div className="fade-up my-12">
              <div className="eyebrow color--grey-4 mb-4">
                <AniLink
                  cover
                  direction="up"
                  duration={1}
                  bg="color--grey-3"
                  to="/blog">
                  Blog
                </AniLink>{' '}
                / {getBlogDisplayDate(post.date, 'en-US')} /{' '}
                {post.author?.node?.name && (
                  <AniLink
                    cover
                    className="underline"
                    direction="up"
                    duration={1}
                    bg="color--grey-3"
                    to={`/author/${post.author?.node?.slug}`}>
                    {post.author?.node?.name}
                  </AniLink>
                )}
                {` / UPDATED ${getBlogDisplayDate(post.modified, 'en-US')}`}
              </div>
              <h1 className="h2 color--primary-dark">{post.title}</h1>
              <div className="mt-12 flex items-center">
                <div className="flex">
                  <a
                    href={`https://twitter.com/intent/tweet?url=https://cloudkitchens.com/${post.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="db mr-3">
                    <TwitterIcon />
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=https://cloudkitchens.com/${post.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="db mr-3">
                    <FacebookIcon />
                  </a>
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=https://cloudkitchens.com/${post.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="db mr-6">
                    <LinkedinIcon />
                  </a>
                </div>
                <div className="flex gap-2 border-l border-grey-4 pl-6 align-top">
                  <Button
                    href="/blog"
                    size="sm"
                    variant="secondary"
                    emphasis="medium">
                    View all
                  </Button>
                  <Button globalTypeform size="sm">
                    LEARN MORE
                  </Button>
                </div>
              </div>
            </div>
            <div className="table-of-contents">
              <h3 className="h3 color--primary mb-4">Table of Contents</h3>
              <ul className="color--grey-5 list-none text-sm leading-relaxed tracking-[0.01em]">
                {Array.from(headings2).map((h2) => (
                  <li
                    key={h2.id}
                    className="mt-1 marker:font-medium marker:text-primary">
                    <a
                      href={`#${h2.id}`}
                      className="transition-opacity hover:opacity-70">
                      {h2.textContent}
                    </a>
                  </li>
                ))}
              </ul>
              {banner && (
                <Banner className="color--white relative mt-8 bg-primary">
                  <div className="px-4 py-8 lg:w-1/2">
                    <div className="eyebrow p5 line color--grey-1 fade-up mb-10 mb-8">
                      <PrismicRichText field={banner?.eyebrow?.richText} />
                    </div>
                    <div className="h5">
                      <PrismicRichText field={banner?.content?.richText} />
                    </div>
                    {banner?.typeform_id && (
                      <div className="mt-8">
                        <Button
                          globalTypeform
                          typeform_id={banner?.typeform_id}
                          size="lg"
                          variant="secondary"
                          className="md-down:w-full md-down:[&_*]:w-full">
                          Tour a kitchen
                        </Button>
                      </div>
                    )}
                  </div>
                  <img
                    className="absolute right-0 top-0 hidden h-full w-1/2 object-cover lg:block"
                    src={banner?.image1?.url}
                    alt=""
                  />
                </Banner>
              )}
            </div>
          </div>
          <BlogContent className="blog--content col-span-4 pt-6 md:col-span-6 md:col-start-7">
            <div className="blog">{parsedContent}</div>
            <Button
              globalTypeform
              size="lg"
              className="mt-4 md-down:w-full md:mt-8 md-down:[&_*]:w-full">
              LEARN MORE
            </Button>
          </BlogContent>
        </div>
      </div>
    </div>
  );
};

export default BlockPost;
